import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CaseFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialData = null,
  language = 'en',
  direction = 'ltr' 
}) => {
  const [formData, setFormData] = useState({
    caseNumber: '',
    clientId: '',
    practiceArea: '',
    caseType: '',
    assignedLawyer: '',
    status: 'intake',
    openedDate: new Date()?.toISOString()?.split('T')?.[0],
    description: '',
    legalNotes: '',
    courtName: '',
    nextHearing: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        caseNumber: '',
        clientId: '',
        practiceArea: '',
        caseType: '',
        assignedLawyer: '',
        status: 'intake',
        openedDate: new Date()?.toISOString()?.split('T')?.[0],
        description: '',
        legalNotes: '',
        courtName: '',
        nextHearing: ''
      });
    }
    setErrors({});
  }, [initialData, isOpen]);

  const labels = {
    title: {
      en: initialData ? 'Edit Case' : 'Create New Case',
      fr: initialData ? 'Modifier le dossier' : 'Créer un nouveau dossier',
      ar: initialData ? 'تعديل القضية' : 'إنشاء قضية جديدة'
    },
    caseNumber: {
      en: 'Case Number',
      fr: 'Numéro de dossier',
      ar: 'رقم القضية'
    },
    client: {
      en: 'Client',
      fr: 'Client',
      ar: 'العميل'
    },
    practiceArea: {
      en: 'Practice Area',
      fr: 'Domaine de pratique',
      ar: 'مجال الممارسة'
    },
    caseType: {
      en: 'Case Type',
      fr: 'Type de dossier',
      ar: 'نوع القضية'
    },
    assignedLawyer: {
      en: 'Assigned Lawyer',
      fr: 'Avocat assigné',
      ar: 'المحامي المعين'
    },
    status: {
      en: 'Status',
      fr: 'Statut',
      ar: 'الحالة'
    },
    openedDate: {
      en: 'Opened Date',
      fr: 'Date d\'ouverture',
      ar: 'تاريخ الفتح'
    },
    description: {
      en: 'Case Description',
      fr: 'Description du dossier',
      ar: 'وصف القضية'
    },
    legalNotes: {
      en: 'Legal Notes',
      fr: 'Notes juridiques',
      ar: 'ملاحظات قانونية'
    },
    courtName: {
      en: 'Court Name',
      fr: 'Nom du tribunal',
      ar: 'اسم المحكمة'
    },
    nextHearing: {
      en: 'Next Hearing Date',
      fr: 'Date de la prochaine audience',
      ar: 'تاريخ الجلسة القادمة'
    },
    cancel: {
      en: 'Cancel',
      fr: 'Annuler',
      ar: 'إلغاء'
    },
    save: {
      en: 'Save Case',
      fr: 'Enregistrer le dossier',
      ar: 'حفظ القضية'
    },
    selectClient: {
      en: 'Select a client',
      fr: 'Sélectionner un client',
      ar: 'اختر عميلاً'
    },
    selectArea: {
      en: 'Select practice area',
      fr: 'Sélectionner le domaine',
      ar: 'اختر المجال'
    },
    selectLawyer: {
      en: 'Select a lawyer',
      fr: 'Sélectionner un avocat',
      ar: 'اختر محامياً'
    }
  };

  const clientOptions = [
    { value: '', label: labels?.selectClient?.[language] },
    { value: 'client-1', label: 'Mohammed Alami' },
    { value: 'client-2', label: 'Fatima Bennani' },
    { value: 'client-3', label: 'Hassan Tazi' },
    { value: 'client-4', label: 'Amina Chraibi' },
    { value: 'client-5', label: 'Rachid Fassi' }
  ];

  const practiceAreaOptions = [
    { value: '', label: labels?.selectArea?.[language] },
    { 
      value: 'civil', 
      label: language === 'ar' ? 'القانون المدني' : language === 'fr' ? 'Droit civil' : 'Civil Law' 
    },
    { 
      value: 'commercial', 
      label: language === 'ar' ? 'القانون التجاري' : language === 'fr' ? 'Droit commercial' : 'Commercial Law' 
    },
    { 
      value: 'family', 
      label: language === 'ar' ? 'قانون الأسرة' : language === 'fr' ? 'Droit de la famille' : 'Family Law' 
    },
    { 
      value: 'criminal', 
      label: language === 'ar' ? 'القانون الجنائي' : language === 'fr' ? 'Droit pénal' : 'Criminal Law' 
    },
    { 
      value: 'labor', 
      label: language === 'ar' ? 'قانون العمل' : language === 'fr' ? 'Droit du travail' : 'Labor Law' 
    },
    { 
      value: 'real-estate', 
      label: language === 'ar' ? 'القانون العقاري' : language === 'fr' ? 'Droit immobilier' : 'Real Estate Law' 
    }
  ];

  const lawyerOptions = [
    { value: '', label: labels?.selectLawyer?.[language] },
    { value: 'ahmed-benali', label: 'Ahmed Benali' },
    { value: 'fatima-zahra', label: 'Fatima Zahra El Amrani' },
    { value: 'youssef-mansouri', label: 'Youssef Mansouri' },
    { value: 'sara-idrissi', label: 'Sara Idrissi' },
    { value: 'karim-alaoui', label: 'Karim Alaoui' }
  ];

  const statusOptions = [
    { 
      value: 'intake', 
      label: language === 'ar' ? 'استقبال' : language === 'fr' ? 'Réception' : 'Intake' 
    },
    { 
      value: 'investigation', 
      label: language === 'ar' ? 'تحقيق' : language === 'fr' ? 'Enquête' : 'Investigation' 
    },
    { 
      value: 'litigation', 
      label: language === 'ar' ? 'تقاضي' : language === 'fr' ? 'Contentieux' : 'Litigation' 
    },
    { 
      value: 'negotiation', 
      label: language === 'ar' ? 'تفاوض' : language === 'fr' ? 'Négociation' : 'Negotiation' 
    },
    { 
      value: 'appeal', 
      label: language === 'ar' ? 'استئناف' : language === 'fr' ? 'Appel' : 'Appeal' 
    },
    { 
      value: 'closed', 
      label: language === 'ar' ? 'مغلق' : language === 'fr' ? 'Clôturé' : 'Closed' 
    }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.caseNumber?.trim()) {
      newErrors.caseNumber = language === 'ar' ? 'رقم القضية مطلوب' : language === 'fr' ? 'Numéro de dossier requis' : 'Case number required';
    }
    if (!formData?.clientId) {
      newErrors.clientId = language === 'ar' ? 'العميل مطلوب' : language === 'fr' ? 'Client requis' : 'Client required';
    }
    if (!formData?.practiceArea) {
      newErrors.practiceArea = language === 'ar' ? 'مجال الممارسة مطلوب' : language === 'fr' ? 'Domaine requis' : 'Practice area required';
    }
    if (!formData?.assignedLawyer) {
      newErrors.assignedLawyer = language === 'ar' ? 'المحامي مطلوب' : language === 'fr' ? 'Avocat requis' : 'Lawyer required';
    }
    if (!formData?.description?.trim()) {
      newErrors.description = language === 'ar' ? 'الوصف مطلوب' : language === 'fr' ? 'Description requise' : 'Description required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[200]" onClick={onClose} />
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4 overflow-y-auto">
        <div 
          className="bg-card border border-border rounded-lg w-full max-w-4xl my-8 shadow-elevation-lg"
          style={{ direction }}
          onClick={(e) => e?.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
            <h2 className="text-lg md:text-xl font-semibold text-foreground">
              {labels?.title?.[language]}
            </h2>
            <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <Input
                label={labels?.caseNumber?.[language]}
                type="text"
                required
                value={formData?.caseNumber}
                onChange={(e) => handleChange('caseNumber', e?.target?.value)}
                error={errors?.caseNumber}
                placeholder="2024-156"
              />

              <Select
                label={labels?.client?.[language]}
                required
                options={clientOptions}
                value={formData?.clientId}
                onChange={(value) => handleChange('clientId', value)}
                error={errors?.clientId}
              />

              <Select
                label={labels?.practiceArea?.[language]}
                required
                options={practiceAreaOptions}
                value={formData?.practiceArea}
                onChange={(value) => handleChange('practiceArea', value)}
                error={errors?.practiceArea}
              />

              <Input
                label={labels?.caseType?.[language]}
                type="text"
                value={formData?.caseType}
                onChange={(e) => handleChange('caseType', e?.target?.value)}
                placeholder={language === 'ar' ? 'نوع القضية' : language === 'fr' ? 'Type de dossier' : 'Case type'}
              />

              <Select
                label={labels?.assignedLawyer?.[language]}
                required
                options={lawyerOptions}
                value={formData?.assignedLawyer}
                onChange={(value) => handleChange('assignedLawyer', value)}
                error={errors?.assignedLawyer}
              />

              <Select
                label={labels?.status?.[language]}
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => handleChange('status', value)}
              />

              <Input
                label={labels?.openedDate?.[language]}
                type="date"
                required
                value={formData?.openedDate}
                onChange={(e) => handleChange('openedDate', e?.target?.value)}
              />

              <Input
                label={labels?.nextHearing?.[language]}
                type="date"
                value={formData?.nextHearing}
                onChange={(e) => handleChange('nextHearing', e?.target?.value)}
              />

              <div className="md:col-span-2">
                <Input
                  label={labels?.courtName?.[language]}
                  type="text"
                  value={formData?.courtName}
                  onChange={(e) => handleChange('courtName', e?.target?.value)}
                  placeholder={language === 'ar' ? 'محكمة الاستئناف بالدار البيضاء' : language === 'fr' ? 'Cour d\'appel de Casablanca' : 'Court of Appeal Casablanca'}
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {labels?.description?.[language]} <span className="text-error">*</span>
                </label>
                <textarea
                  className="w-full min-h-[120px] px-3 py-2 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                  value={formData?.description}
                  onChange={(e) => handleChange('description', e?.target?.value)}
                  placeholder={language === 'ar' ? 'وصف تفصيلي للقضية...' : language === 'fr' ? 'Description détaillée du dossier...' : 'Detailed case description...'}
                />
                {errors?.description && (
                  <p className="text-xs text-error mt-1">{errors?.description}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {labels?.legalNotes?.[language]}
                </label>
                <textarea
                  className="w-full min-h-[120px] px-3 py-2 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                  value={formData?.legalNotes}
                  onChange={(e) => handleChange('legalNotes', e?.target?.value)}
                  placeholder={language === 'ar' ? 'ملاحظات قانونية إضافية...' : language === 'fr' ? 'Notes juridiques supplémentaires...' : 'Additional legal notes...'}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 mt-6 pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={onClose}
              >
                {labels?.cancel?.[language]}
              </Button>
              <Button
                type="submit"
                variant="default"
                fullWidth
                iconName="Save"
                iconPosition="left"
              >
                {labels?.save?.[language]}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CaseFormModal;